import { z } from 'zod';
import { TopicPath } from './fields.js';
import { Uuid } from './primitives.js';
// Flashcards phase 2 (ADR 0043): AI drafts for a deck, checked, de-duplicated and then reviewed
// by a person like every other card. Behind the per-product switch `flashcards_ai_generate`.
/** The same limits as the `flashcards` table checks and the phase 1 editor. */
export const FLASHCARD_LIMITS = { front: 500, back: 1000 };
/** Most cards one run may draft (cost and review time stay small). */
export const CARD_GEN_MAX_PER_RUN = 50;
/** The per-product feature switch; off means the Generate tab and API refuse. */
export const FLASHCARDS_AI_GENERATE_FLAG = 'flashcards_ai_generate';
export const GenerateCardsRequest = z
    .object({
    deckId: Uuid,
    /** The exam the cards are for (active pack key); material for it and for every exam is used. */
    examKey: z.string().trim().min(1).max(40),
    /** JE branch (civil / electrical / mechanical), when the exam has branches. */
    branchKey: z.string().trim().min(1).max(40).optional(),
    /** Syllabus topics: excerpts from the knowledge base for each topic. */
    topicPaths: z.array(TopicPath).max(20).default([]),
    /** Knowledge-base sources: with topics they narrow the search, alone they are read in order. */
    sourceIds: z.array(Uuid).max(20).default([]),
    /** Approved questions for these topics are turned into cards. */
    questionTopics: z.array(TopicPath).max(10).default([]),
    count: z.number().int().min(1).max(CARD_GEN_MAX_PER_RUN),
    notes: z.string().trim().max(300).optional(),
})
    .strict()
    .refine((v) => v.topicPaths.length + v.sourceIds.length + v.questionTopics.length > 0, {
    message: 'choose at least one topic, source or question topic',
});
export const CardGenEstimateQuery = z
    .object({ count: z.coerce.number().int().min(1).max(CARD_GEN_MAX_PER_RUN) })
    .strict();
export const CardGenEstimateDto = z.object({
    perCardPaise: z.number(),
    totalPaise: z.number(),
    usdInr: z.number(),
    missingPrices: z.array(z.string()),
});
export const CardGenStatusDto = z.object({
    enabled: z.boolean(),
    maxPerRun: z.number().int(),
});
export const CardGenCountsDto = z.object({
    drafted: z.number().int(),
    passed: z.number().int(),
    flagged: z.number().int(),
    failed: z.number().int(),
    duplicates: z.number().int(),
    dropped: z.number().int(),
    thin: z.number().int(),
    errors: z.number().int(),
});
export const CardGenRunStatus = z.enum([
    'queued',
    'running',
    'paused',
    'done',
    'failed',
    'cancelled',
]);
export const CardGenRunDto = z.object({
    id: Uuid,
    deckId: Uuid,
    deckTitle: z.string(),
    status: CardGenRunStatus,
    spec: z.object({
        examKey: z.string(),
        branchKey: z.string().optional(),
        topicPaths: z.array(z.string()),
        sourceIds: z.array(z.string()),
        questionTopics: z.array(z.string()),
        count: z.number().int(),
        notes: z.string().optional(),
    }),
    counts: CardGenCountsDto,
    estimatePaise: z.number(),
    costPaise: z.number(),
    error: z.string().nullable(),
    createdAt: z.string(),
    finishedAt: z.string().nullable(),
});
export const CardCitationDto = z.object({
    kind: z.enum(['chunk', 'item']),
    /** Chunk id or question id. */
    id: Uuid,
    sourceId: Uuid.nullable(),
    title: z.string(),
    page: z.number().int().nullable(),
});
export const CheckVerdict = z.enum(['pass', 'flag', 'fail']);
export const GenCardDto = z.object({
    id: Uuid,
    front: z.string(),
    back: z.string(),
    status: z.enum(['draft', 'approved', 'rejected', 'archived']),
    rejectReason: z.string().nullable(),
    citations: z.array(CardCitationDto),
    check: CheckVerdict,
    checkReason: z.string().nullable(),
    duplicate: CheckVerdict,
    duplicateOf: z.object({ id: Uuid, front: z.string(), score: z.number() }).nullable(),
});
export const CardReportsDto = z.object({
    decks: z.array(z.object({
        deckId: Uuid,
        title: z.string(),
        draft: z.number().int(),
        approved: z.number().int(),
        rejected: z.number().int(),
        archived: z.number().int(),
    })),
    runs: z.object({
        runs: z.number().int(),
        drafted: z.number().int(),
        passed: z.number().int(),
        duplicates: z.number().int(),
        costPaise: z.number(),
        /** Share of drafted cards the checker passed (0..1), null before any run. */
        passRate: z.number().nullable(),
    }),
    /** AI drafts a person has decided on: approved / (approved + rejected), null before any. */
    approvalRate: z.number().nullable(),
    decided: z.number().int(),
});
//# sourceMappingURL=flashcards-ai.js.map